import { nonnull } from "@tento-core/assert";
import { MathUtils } from "@tento-core/math";
import { NumberRange, NumRangeLike } from "@tento-core/range";
import { Tracing } from "@tento-core/tracing";

export class ScrollWindow {
  private offsets: number[] = [0];
  private didEmitZeroIndex = false;
  /** @see {@link reportRangeClamped} — the last clamp announced, so a viewport
   *  parked outside the map reports once per state, not once per read. */
  private lastClampKey: Option<string> = null;
  /** @see {@link reportReachClamped} — same latch, different question: a
   *  viewport parked ON the axis edge re-asks for the same walk every frame. */
  private lastReachKey: Option<string> = null;
  offsetBase: number = 0;

  private _maxIndex: number = 0;
  get maxIndex() {
    return this._maxIndex;
  }
  get maxOffset(): number {
    return nonnull(this.offsets[this._maxIndex]) + this.offsetBase;
  }

  private _minIndex: number = 0;
  get minIndex() {
    return this._minIndex;
  }
  get minOffset(): number {
    return nonnull(this.offsets[this._minIndex]) + this.offsetBase;
  }

  /**
   * The furthest offsets a viewport may occupy — the pixel edges of the index
   * band the delegate calls addressable ({@link ScrollWindow.Delegate.reachableIndices}).
   * `minOffset`/`maxOffset` say how far the map HAS been walked; these say how
   * far it is ever allowed to go.
   *
   * Both answer `±Infinity` — "no limit I can state yet" — in two cases that
   * are one case: an unbounded space (a plain list: index 3 means row 3 and
   * every row is addressable), and a bounded space whose edge index the map has
   * not reached. The second is not a gap. The clamp exists to stop a viewport
   * from PRESENTING an off-axis column, and a viewport that has not scrolled to
   * the edge is not presenting one; when it does scroll out,
   * {@link extendToOffsetRange} walks to the reach index and stops there, so
   * the offset becomes readable at exactly the moment it starts to bind. The
   * two halves need each other: the walk's refusal is what makes the number
   * knowable, and the number is what turns the refusal into a scroll limit
   * instead of a dead zone at the end of the strip.
   *
   * Two getters rather than one range so the scroll path pays no allocation —
   * the scrollable asks on every scroll event.
   */
  get minReach(): number {
    const band = this.reachBand;
    if (band == null) return -Infinity;
    return this.getOffset(band.min) ?? -Infinity;
  }

  /** @see {@link minReach} */
  get maxReach(): number {
    const band = this.reachBand;
    if (band == null) return Infinity;
    return this.getOffset(band.max) ?? Infinity;
  }

  /**
   * The delegate's band, or `null` when there is nothing this window can
   * enforce — the single reader, so the getters above and the walk below can
   * never disagree about whether a limit is in force.
   *
   * Two ways to have no band. `unbounded` is the delegate saying so. The other
   * is a band that does not contain index 0, and refusing THAT is the
   * interesting half: this map is a prefix sum anchored at 0 and grown outward,
   * so a band excluding the anchor is one no walk from here can honour — the
   * first step in the band's direction is already outside it, both loops stop
   * immediately, and the window freezes at a single column. That is a worse
   * failure than the one the reach exists to prevent, and it is not
   * hypothetical: it is what a sheet anchored centuries from today would
   * produce. So the band is dropped and the pre-existing guards take over —
   * `witness/viewport-range` then fires on the render, which is the right way
   * for "this sheet is anchored off the calendar" to surface.
   */
  private get reachBand(): Option<{ min: number; max: number }> {
    const reach = this.delegate.reachableIndices();
    if (reach.kind !== "bounded") return null;
    if (reach.min > 0 || reach.max < 0) return null;
    return reach;
  }

  constructor(readonly delegate: ScrollWindow.Delegate) {}

  maybeEmitZeroIndex() {
    if (this.didEmitZeroIndex) return;
    this.delegate.onOffsetChanged(0, 0);
    this.didEmitZeroIndex = true;
  }

  reset(reason: string) {
    // Sentinel: a window reset zeroes the index↔pixel map and offsetBase (snapping
    // the view). Legitimate only on a geometry re-anchor — cell-width/viewport
    // change ("layout") or sheet/timescale navigation ("context-reanchor"). A reset
    // during steady-state scroll, or with any other reason, is a regression.
    Tracing.log("scroll/window-reset", { reason });
    this.offsets = [0];
    this._minIndex = 0;
    this._maxIndex = 0;
    this.didEmitZeroIndex = false;
    this.offsetBase = 0;
    // A new map is a new set of reachable pixels: the clamp the old one
    // announced says nothing about this one.
    this.lastClampKey = null;
    this.lastReachKey = null;
  }

  setOffset(index: number, offset: number) {
    this.offsets[index] = MathUtils.roundToDecimals(offset, 3);
    if (index > this._maxIndex) {
      this._maxIndex = index;
    }
    if (index < this._minIndex) {
      this._minIndex = index;
    }
    this.delegate.onOffsetChanged(index, offset);
  }

  getOffset(index: number): Option<number> {
    const v = this.offsets[index];
    return v != null ? v + this.offsetBase : undefined;
  }

  findIndexForOffsetMin(offset: number): Option<number> {
    const rawOffset = offset - this.offsetBase;
    const min = this._minIndex;
    const max = this._maxIndex;
    if (rawOffset > this.offsets[max]) return null;
    if (rawOffset < this.offsets[min]) return null;
    // offsets is monotonically non-decreasing, so the predicate
    // `offsets[i] - rawOffset >= EPSILON` (strictly greater, with float
    // tolerance) is monotone (false then true). Bisect for the first true
    // index and return its predecessor — the cell CONTAINING the offset.
    // Do not floor either side: an earlier `Math.floor` on the stored
    // offset alone made the search exact only for integral widths, and
    // overshot by ~1/width cells on fractional geometry such as
    // `viewportWidth / n` (caught by the scroll-window fuzzer).
    let lo = min + 1;
    let hi = max;
    let firstTrue: Option<number> = null;
    while (lo <= hi) {
      const mid = lo + ((hi - lo) >> 1);
      if (this.offsets[mid] - rawOffset >= Number.EPSILON) {
        firstTrue = mid;
        hi = mid - 1;
      } else {
        lo = mid + 1;
      }
    }
    return firstTrue == null ? max : firstTrue - 1;
  }

  /**
   * The index range covering a PIXEL range — always in indices this window HAS.
   *
   * Both lookups used to be `!`-asserted, which is how a `NumberRange` whose
   * fields are typed `number` came to hold runtime nulls: the search answers
   * `null` for any pixel outside the walked span, and `ScrollEngine.State`
   * `.reconcile` then ran `clamp` and `changeset` over the result.
   *
   * Of the two honest repairs — hand back an `Option<NumberRange>` for the
   * caller to branch on, or answer with the part of the request this window can
   * actually name — this is the second. Every caller (`ScrollViewport.visible`
   * and `.rendered`, the navigator emit) is asking "which columns do I paint",
   * from inside the render path, and there IS a truthful answer to give them:
   * the span the window reached. An `Option` would hand a state none of those
   * sites can repair to all of them, and the repair each would then have to
   * invent is exactly this clamp.
   *
   * The painted range is limited to the intersection of the walked span and
   * the space's reach. An index prewalk may retain padding beyond reach for
   * re-anchoring; that padding is addressable map data, not paintable content.
   * The clamp is not silent:
   * `scroll/window-range-clamped` carries the request and the span it was
   * pulled into. It is not by itself an error, either: pixels the map does not
   * cover are what a short {@link extendToOffsetRange} leaves behind, and the
   * reconcile reports THAT where it happens.
   */
  findIndiciesFromOffsetRange(offsets: NumRangeLike): NumberRange {
    const start = this.indexForReachedOffset(Math.ceil(offsets.start));
    const end = this.indexForReachedOffset(Math.floor(offsets.end));
    const reach = this.reachBand;
    const min = Math.max(this._minIndex, reach?.min ?? this._minIndex);
    const max = Math.min(this._maxIndex, reach?.max ?? this._maxIndex);
    const first = Math.max(min, Math.min(max, start.index));
    const last = Math.max(min, Math.min(max, end.index));
    if (start.clamped || end.clamped || first !== start.index || last !== end.index) {
      this.reportRangeClamped(offsets);
    }
    return new NumberRange(first, last);
  }

  /**
   * {@link findIndexForOffsetMin} with the query first pulled into the span the
   * window reached, so it cannot miss.
   *
   * The bounds are tested in the window's OWN raw space (`offset - offsetBase`,
   * the identical expression the search evaluates) rather than against
   * `minOffset`/`maxOffset`: adding `offsetBase` and taking it off again is
   * lossy, and a bound that came back one ulp outside would make the search
   * miss anyway — reopening the very hole this closes. `!(raw >= lo)` rather
   * than `raw < lo` so a NaN query lands on an edge instead of falling through
   * as "inside".
   */
  private indexForReachedOffset(
    offset: number,
  ): { index: number; clamped: boolean } {
    const raw = offset - this.offsetBase;
    if (!(raw >= this.offsets[this._minIndex])) {
      return { index: this._minIndex, clamped: true };
    }
    if (raw > this.offsets[this._maxIndex]) {
      return { index: this._maxIndex, clamped: true };
    }
    const found = this.findIndexForOffsetMin(offset);
    if (found != null) return { index: found, clamped: false };
    // Unreachable: the two guards above are the exact negation of the search's
    // two `null` returns, over the same numbers. If it ever does fire, the map
    // has broken its own contract — so say so with the numbers, and answer with
    // an edge rather than throwing, because every caller here is inside
    // `State.reconcile` where an exception takes the sheet down with it.
    const payload = {
      offset,
      offsetBase: this.offsetBase,
      minIndex: this._minIndex,
      maxIndex: this._maxIndex,
    };
    Tracing.err("ui")("scroll-window/offset-unmapped-inside-window", payload);
    Tracing.log("witness/scroll-window-range", payload);
    return { index: this._maxIndex, clamped: true };
  }

  /**
   * One breadcrumb per distinct clamp, not per read: `visible` and `rendered`
   * resolve two bounds each per reconcile, and a viewport parked outside the
   * map reconciles every frame — the latch is what keeps an observable clamp
   * from becoming a log storm. Mirrors `ScrollEngine`'s coverage-repair latch.
   */
  private reportRangeClamped(offsets: NumRangeLike): void {
    const key = `${offsets.start}:${offsets.end}:${this._minIndex}:` +
      `${this._maxIndex}:${this.offsetBase}`;
    if (key === this.lastClampKey) return;
    this.lastClampKey = key;
    Tracing.log("scroll/window-range-clamped", {
      from: offsets.start,
      to: offsets.end,
      reachedFrom: this.minOffset,
      reachedTo: this.maxOffset,
      minIndex: this._minIndex,
      maxIndex: this._maxIndex,
    });
  }

  /**
   * Grow the window until it covers a PIXEL range, one cell width at a time.
   *
   * Two ways this walk can run away, and it stops loudly for both rather than
   * spinning:
   *
   * - **No progress.** The loops terminate on OFFSET progress but advance by
   *   `widthForIndex` per iteration, so a delegate stuck on non-positive widths
   *   never converges (caught by the scroll-window fuzzer). A short run of
   *   zero-width cells is legal and walked through; a run past
   *   {@link ScrollWindow.EXTEND_STALL_LIMIT} means the range is unsatisfiable.
   * - **Progress that is too small to matter.** A pixel distance divided by a
   *   sub-pixel width is a five-to-eight-figure index count, and every one of
   *   them is an array slot. This is the walk R19's OOM actually came through:
   *   the translated-anchor guard (`ScrollEngine.isAddressableAnchor`) had
   *   already rejected the bad index, and `extendToIdxRange` was never asked to
   *   reach it — but the reconcile that follows the layout rebind asks for the
   *   scroller's live offset against a window that `markLayoutDirtied` has just
   *   RESET to a single index, and at a degenerate cell width (a 1460px viewport
   *   minus a near-full-width legend, split by a high column count, resolves
   *   near 1px) the full ±8M-px strip is tens of millions of entries. So the
   *   span has the same ceiling the index walk has.
   *
   * Either way the window is left SHORT rather than unwound: it is a prefix sum,
   * a partial one is still correct for the indices it holds, and later extends
   * keep working. `extendToIdxRange` refuses outright instead, because it can —
   * it is called to REACH an anchor, and reaching half of one is worthless.
   *
   * And either way it SAYS so, in the same vocabulary that walk answers in
   * ({@link ScrollWindow.ExtendOutcome}). Breaking the loop stops the spin but
   * leaves the caller believing the pixels it asked for are covered: the
   * reconcile that owns this call goes straight on to resolve those same pixels
   * into indices, so "the window is shorter than you asked" has to be
   * expressible at the return, not only in a log line.
   */
  extendToOffsetRange(
    offsets: NumRangeLike,
  ): ScrollWindow.ExtendOutcome.Extended | ScrollWindow.ExtendOutcome.Short {
    this.maybeEmitZeroIndex();
    const rawStart = offsets.start - this.offsetBase;
    const rawEnd = offsets.end - this.offsetBase;
    const max = ScrollWindow.MAX_REANCHOR_SPAN;
    // The FIRST end to fall short names the outcome; `reached` below tells the
    // whole truth when both did, and the two loops log their own ends anyway.
    let short: Option<Pick<ScrollWindow.ExtendOutcome.Short, "reason" | "dir">> =
      null;
    let stall = 0;
    // Read once per call, not per step: the band is a property of the space,
    // and nothing inside these loops can change it.
    const reach = this.reachBand;
    while (rawStart < this.offsets[this._minIndex]) {
      if (reach != null && this._minIndex <= reach.min) {
        this.reportReachClamped("min", offsets, reach.min);
        short = { reason: "reach", dir: "min" };
        break;
      }
      if (this._maxIndex - this._minIndex >= max) {
        this.reportSpanCeiling("min", offsets);
        short = { reason: "span-ceiling", dir: "min" };
        break;
      }
      const width = this.delegate.widthForIndex(this.minIndex - 1);
      this.setOffset(this.minIndex - 1, this.offsets[this._minIndex] - width);
      if (width > 0) stall = 0;
      else if (++stall > ScrollWindow.EXTEND_STALL_LIMIT) {
        Tracing.err("ui")("scroll-window/extend-stalled", { dir: "min", at: this.minIndex });
        short = { reason: "stall", dir: "min" };
        break;
      }
    }
    stall = 0;
    while (rawEnd > this.offsets[this._maxIndex]) {
      if (reach != null && this._maxIndex >= reach.max) {
        this.reportReachClamped("max", offsets, reach.max);
        short ??= { reason: "reach", dir: "max" };
        break;
      }
      if (this._maxIndex - this._minIndex >= max) {
        this.reportSpanCeiling("max", offsets);
        short ??= { reason: "span-ceiling", dir: "max" };
        break;
      }
      const width = this.delegate.widthForIndex(this.maxIndex);
      this.setOffset(this.maxIndex + 1, this.offsets[this._maxIndex] + width);
      if (width > 0) stall = 0;
      else if (++stall > ScrollWindow.EXTEND_STALL_LIMIT) {
        Tracing.err("ui")("scroll-window/extend-stalled", { dir: "max", at: this.maxIndex });
        short ??= { reason: "stall", dir: "max" };
        break;
      }
    }
    if (short == null) return ScrollWindow.EXTENDED;
    return {
      kind: "short",
      reason: short.reason,
      dir: short.dir,
      requested: { start: offsets.start, end: offsets.end },
      reached: { start: this.minOffset, end: this.maxOffset },
    };
  }

  /**
   * One line per distinct stop at the axis edge — and deliberately NOT an error
   * and NOT a `witness/`, which is what separates it from every other early
   * return in this file. A span ceiling or a stall means the geometry asked a
   * question the map could not answer; reaching the end of the calendar means
   * the user scrolled to the end of the calendar. It is the designed
   * termination of a legal walk, so it is an info breadcrumb, latched the same
   * way {@link reportRangeClamped} is: a viewport parked at the edge re-asks on
   * every frame.
   */
  private reportReachClamped(
    dir: "min" | "max",
    offsets: NumRangeLike,
    at: number,
  ): void {
    const key = `${dir}:${at}:${this._minIndex}:${this._maxIndex}`;
    if (key === this.lastReachKey) return;
    this.lastReachKey = key;
    Tracing.log("scroll/window-reach-clamped", {
      dir,
      atIndex: at,
      from: offsets.start,
      to: offsets.end,
      reachedFrom: this.minOffset,
      reachedTo: this.maxOffset,
    });
  }

  /** One line per offset walk that hit the ceiling; the `witness/` half is what
   *  the storm fuzzer's battery-wide forbid turns into a failing program. */
  private reportSpanCeiling(dir: "min" | "max", offsets: NumRangeLike): void {
    const payload = {
      span: this._maxIndex - this._minIndex,
      max: ScrollWindow.MAX_REANCHOR_SPAN,
      from: offsets.start,
      to: offsets.end,
      at: `extend-offset-${dir}`,
    };
    Tracing.err("ui")("scroll-window/extend-span-ceiling", payload);
    Tracing.log("witness/scroll-window-span", payload);
  }

  recalculateOffsetsFrom(index: number): void {
    const start = Math.max(index, this._minIndex);
    const end = this._maxIndex;
    for (let i = start; i <= end; i++) {
      const prevOffset = this.offsets[i] ?? 0;
      const width = this.delegate.widthForIndex(i);
      this.setOffset(i + 1, prevOffset + width);
    }
  }

  /**
   * Grow the window until it covers `indices`, one index at a time — or refuse.
   *
   * The walk is linear in the DISTANCE from the live window to the requested
   * range, and every step it takes writes `offsets[i]` at that subscript. Reach
   * for a far negative index and the dense array turns into a dictionary-mode
   * one at ~82 bytes an entry: −26,905,540 (a mapper translation read in the
   * wrong index space, R19) cost 2.2 GB in a single call, against a 4.3 GB heap,
   * and the renderer died before any assertion could name the caller.
   *
   * So the walk has a ceiling. The span measured is the span of the window this
   * call would LEAVE BEHIND — `max(end, maxIndex) - min(start, minIndex)` — not
   * the width of the requested range, because a two-index range 26 M away costs
   * the distance, not the width. Above {@link ScrollWindow.MAX_REANCHOR_SPAN}
   * nothing is written at all and the caller is told; the previous window is
   * still valid, still covers what it covered, and the strip stays painted while
   * the breadcrumb says why it did not move. A non-finite bound refuses by the
   * same comparison (`!(NaN <= max)` is true), which is deliberate: an infinite
   * `end` is the one input that would spin here forever.
   *
   * This is belt-and-braces for {@link ScrollEngine.isAddressableAnchor}, which
   * screens the same arithmetic one layer up. That guard only sees a TRANSLATED
   * anchor; this one is under every caller.
   */
  extendToIdxRange(
    indices: NumRangeLike,
  ): ScrollWindow.ExtendOutcome.Extended | ScrollWindow.ExtendOutcome.Refused {
    const span = Math.max(indices.end, this._maxIndex) -
      Math.min(indices.start, this._minIndex);
    if (!(span <= ScrollWindow.MAX_REANCHOR_SPAN)) {
      return {
        kind: "refused",
        span,
        max: ScrollWindow.MAX_REANCHOR_SPAN,
      };
    }
    this.maybeEmitZeroIndex();
    while (indices.start < this.minIndex) {
      this.setOffset(
        this.minIndex - 1,
        this.offsets[this._minIndex] - this.delegate.widthForIndex(this.minIndex - 1),
      );
    }
    while (indices.end > this.maxIndex) {
      this.setOffset(
        this.maxIndex + 1,
        this.offsets[this._maxIndex] + this.delegate.widthForIndex(this.maxIndex),
      );
    }
    return ScrollWindow.EXTENDED;
  }
}

export namespace ScrollWindow {
  /** Consecutive non-positive widths an offset-driven extend walks through
   * before declaring the range unsatisfiable (see extendToOffsetRange). */
  export const EXTEND_STALL_LIMIT = 512;

  /**
   * The widest window {@link ScrollWindow.extendToIdxRange} will walk to.
   *
   * The bound lives HERE, with the array that pays for it, and
   * {@link ScrollEngine.MAX_REANCHOR_SPAN} is an alias of it — one number, two
   * names for the two layers that check it.
   *
   * It is far above anything real geometry can address: the scroll strip is
   * 2 × baseOffset (~16.7M px) and the narrowest column the sheet resolves is
   * 20px, so ~840k indices span it end to end. 2^21 clears that with room to
   * spare, which is what makes a value beyond it evidence of a broken
   * translation rather than an unusually deep scroll.
   */
  export const MAX_REANCHOR_SPAN = 1 << 21;

  /**
   * What an extend did — ONE way for either walk to say "I could not reach that
   * far", because there is one thing a caller does about it. A boolean would
   * say only that something went wrong; the caller has to log the numbers to
   * make it diagnosable, so each answer carries its own.
   *
   * The two failures are distinct because their post-conditions are:
   * {@link ExtendOutcome.Refused} means nothing was written and the previous
   * window is intact (so a caller may keep using it as-is), while
   * {@link ExtendOutcome.Short} means the walk ran and stopped part-way (so the
   * map is valid but covers LESS than the request, and anything derived from
   * the request has to be read against `reached`). Each walk returns only the
   * pair it can produce: {@link ScrollWindow.extendToIdxRange} refuses whole
   * walks, {@link ScrollWindow.extendToOffsetRange} comes up short.
   */
  export type ExtendOutcome =
    | ExtendOutcome.Extended
    | ExtendOutcome.Refused
    | ExtendOutcome.Short;

  export namespace ExtendOutcome {
    /** The window covers the request. */
    export interface Extended {
      readonly kind: "extended";
    }

    /** Nothing was written: the window is exactly as it was. */
    export interface Refused {
      readonly kind: "refused";
      /** The span the window would have had, had the walk been allowed. */
      readonly span: number;
      readonly max: number;
    }

    /**
     * The walk wrote what it could and stopped: the window is a valid prefix
     * sum over a SMALLER pixel span than was asked for.
     */
    export interface Short {
      readonly kind: "short";
      /**
       * Why it stopped — see {@link ScrollWindow.extendToOffsetRange}.
       *
       * `reach` is the only one of the three that is not a fault: the walk
       * stopped because the request ran off the end of the addressable band
       * ({@link ScrollWindow.Delegate.reachableIndices}), which is a scroll
       * limit, not an unanswerable question.
       */
      readonly reason: "stall" | "span-ceiling" | "reach";
      /** Which end fell short first. */
      readonly dir: "min" | "max";
      /** The pixel range asked for. */
      readonly requested: NumRangeLike;
      /** The pixel range the window covers now — what queries resolve against. */
      readonly reached: NumRangeLike;
    }
  }

  /** The shared success value — an extend that walked is not worth allocating for. */
  export const EXTENDED: ExtendOutcome.Extended = { kind: "extended" };

  /**
   * How far the index space extends — which indices MEAN anything.
   *
   * The window is pure array math over a delegate's widths and has no idea what
   * an index stands for, so it cannot decide this and does not try: it asks. A
   * sheet's columns are dates, and a date axis ends (see
   * `ColumnSpace`/`ViewportRange`); a list's indices are rows, and every row is
   * a row.
   *
   * `unbounded` is a positive claim, not an absent value — the same idiom as
   * {@link ScrollEngine.IndexSpace.identity}: a delegate says "this is not a
   * question I have" rather than leaving a slot unfilled that a reader then has
   * to interpret.
   */
  export type Reach =
    | { readonly kind: "unbounded" }
    | {
      readonly kind: "bounded";
      /** Lowest addressable index, inclusive. */
      readonly min: number;
      /** Highest addressable index, inclusive. */
      readonly max: number;
    };

  export namespace Reach {
    /** The shared no-limit answer — not worth allocating per query. */
    export const UNBOUNDED: Reach = { kind: "unbounded" };

    /** `min`/`max` inclusive. A backwards or non-finite band is no band at all
     *  and answers {@link UNBOUNDED}, because a clamp derived from nonsense
     *  would strand the viewport instead of bounding it. */
    export function bounded(min: number, max: number): Reach {
      if (!Number.isFinite(min) || !Number.isFinite(max)) return UNBOUNDED;
      if (max < min) return UNBOUNDED;
      return { kind: "bounded", min, max };
    }
  }

  export interface Delegate {
    onOffsetChanged(index: number, offset: number): void;
    widthForIndex: (index: number) => number;
    /** @see {@link ScrollWindow.Reach} — asked once per offset walk and once
     *  per {@link ScrollWindow.minReach} read, never per index. */
    reachableIndices(): Reach;
  }
}
